/** Open Robinhood web for a ticker in one reusable named window. */

import { callAgenticTool } from "../api/agenticClient";
import { getDesktopBridge, isStockForgeDesktop } from "./desktopShell";

export const ROBINHOOD_WINDOW_NAME = "sfe-robinhood";

/** Detached Chrome popup — used when Settings → Robinhood → Show on Robinhood is Popup. */
const ROBINHOOD_POPUP_FEATURES = "popup=true,width=480,height=820";
const ROBINHOOD_OPEN_MODE_KEY = "sfe-robinhood-open-mode";

export type RobinhoodOpenMode = "tab" | "popup";

let lastRobinhoodWindow: Window | null = null;

export function loadRobinhoodOpenMode(): RobinhoodOpenMode {
  try {
    return localStorage.getItem(ROBINHOOD_OPEN_MODE_KEY) === "tab" ? "tab" : "popup";
  } catch {
    return "popup";
  }
}

export function saveRobinhoodOpenMode(mode: RobinhoodOpenMode): void {
  try {
    localStorage.setItem(ROBINHOOD_OPEN_MODE_KEY, mode === "tab" ? "tab" : "popup");
  } catch {
    /* ignore quota / private mode */
  }
}

export function robinhoodOpenModeControlsHtml(): string {
  if (isStockForgeDesktop()) {
    return `
    <div class="sfe-rh-settings-block">
      <h5 class="sfe-rh-settings-subtitle">Show on Robinhood</h5>
      <p class="muted small sfe-column-settings-lead">The Mac app uses a grid. Put StockForge+, Original Stock Forge, or Robinhood in each cell. Legend is a title-bar menu that opens the full board or a single widget in its own window. Open in Legend on a row stacks the widgets for that action on the right of the terminal. Sign in with your password — passkeys do not finish in this app. Show on Robinhood uses a Robinhood cell when one is open.</p>
    </div>`;
  }
  const mode = loadRobinhoodOpenMode();
  return `
    <div class="sfe-rh-settings-block">
      <h5 class="sfe-rh-settings-subtitle">Show on Robinhood</h5>
      <p class="muted small sfe-column-settings-lead">Open the contract in a browser tab or a small popup window.</p>
      <div class="sfe-rh-open-mode" role="radiogroup" aria-label="Show on Robinhood">
        <label class="sfe-rh-open-mode-option">
          <input type="radio" name="sfe-rh-open-mode" value="tab"${mode === "tab" ? " checked" : ""} />
          <span>Tab</span>
        </label>
        <label class="sfe-rh-open-mode-option">
          <input type="radio" name="sfe-rh-open-mode" value="popup"${mode === "popup" ? " checked" : ""} />
          <span>Popup</span>
        </label>
      </div>
    </div>`;
}

export function bindRobinhoodOpenModeControls(host: ParentNode): () => void {
  const onChange = (event: Event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name !== "sfe-rh-open-mode") return;
    if (input.value === "tab" || input.value === "popup") saveRobinhoodOpenMode(input.value);
  };
  host.addEventListener("change", onChange);
  return () => host.removeEventListener("change", onChange);
}

const instrumentIdCache = new Map<string, { id: string; chainId: string | null; at: number }>();
const chainIdCache = new Map<string, { id: string; at: number }>();
const INSTRUMENT_CACHE_MS = 10 * 60 * 1000;
const ROBINHOOD_APPLINK_HOST = "https://applink.robinhood.com";

export type RobinhoodOptionSide = "put" | "call";

export type RobinhoodOpenIntent =
  | { kind: "stock"; ticker: string }
  | {
      kind: "sell-put" | "sell-call" | "btc";
      ticker: string;
      side?: string;
      strike?: number | null;
      expiration?: string | null;
      optionId?: string | null;
      chainId?: string | null;
    };

function robinhoodSymbol(ticker: string): string {
  return String(ticker ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.]/g, "");
}

export function robinhoodOptionSide(side: string | null | undefined): RobinhoodOptionSide {
  return String(side ?? "")
    .trim()
    .toLowerCase()
    .includes("call")
    ? "call"
    : "put";
}

export function robinhoodExpirationDate(raw: string | null | undefined): string | null {
  const text = String(raw ?? "").trim();
  const iso = text.match(/(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : null;
}

function robinhoodOptionId(raw: string | null | undefined): string | null {
  const id = String(raw ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ? id
    : null;
}

export function robinhoodOptionsChainUrl(ticker: string): string | null {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  return `https://robinhood.com/options/chains/${encodeURIComponent(symbol)}`;
}

export function robinhoodStockUrl(ticker: string): string | null {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  return `https://robinhood.com/us/en/stocks/${encodeURIComponent(symbol)}/`;
}

/** AASA path on robinhood.com — `/us/en/stocks/` is website-only and stays in Safari. */
export function robinhoodAppStockUrl(ticker: string): string | null {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  return `https://robinhood.com/stocks/${encodeURIComponent(symbol)}`;
}

export function robinhoodSellStrategyUrl(
  ticker: string,
  side: RobinhoodOptionSide,
): string | null {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  const strategy = side === "call" ? "short_call" : "short_put";
  return `https://robinhood.com/options/chains/${encodeURIComponent(symbol)}/builder/${strategy}`;
}

export function robinhoodOptionInstrumentUrl(optionId: string | null | undefined): string | null {
  const id = robinhoodOptionId(optionId);
  if (!id) return null;
  return `https://robinhood.com/options/instruments/${encodeURIComponent(id)}`;
}

function instrumentCacheKey(
  ticker: string,
  expiration: string,
  side: RobinhoodOptionSide,
  strike: number,
): string {
  return `${robinhoodSymbol(ticker)}|${expiration}|${side}|${strike}`;
}

function unwrapToolPayload(result: unknown): unknown {
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return unwrapToolPayload(JSON.parse(trimmed));
      } catch {
        return result;
      }
    }
    return result;
  }
  if (!result || typeof result !== "object" || Array.isArray(result)) return result;
  const record = result as Record<string, unknown>;
  if (Array.isArray(record.content)) {
    for (const block of record.content) {
      if (!block || typeof block !== "object" || Array.isArray(block)) continue;
      const text = (block as { text?: unknown }).text;
      if (typeof text === "string" && text.trim()) {
        return unwrapToolPayload(text);
      }
    }
  }
  if (record.data && typeof record.data === "object") return record.data;
  if (record.result && typeof record.result === "object") return unwrapToolPayload(record.result);
  return result;
}

function parseInstrumentRecord(result: unknown): { id: string; chainId: string | null } | null {
  const payload = unwrapToolPayload(result);
  const record =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  const instruments = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.instruments)
      ? record.instruments
      : [];
  for (const item of instruments) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item as { id?: unknown; chain_id?: unknown };
    const id = robinhoodOptionId(row.id as string);
    if (!id) continue;
    return { id, chainId: robinhoodOptionId(row.chain_id as string) };
  }
  const id = record ? robinhoodOptionId(record.id as string) : null;
  if (!id) return null;
  return { id, chainId: robinhoodOptionId(record.chain_id as string) };
}

export async function resolveRobinhoodOptionInstrumentId(input: {
  ticker: string;
  expiration?: string | null;
  side?: string | null;
  strike?: number | null;
}): Promise<string | null> {
  const symbol = robinhoodSymbol(input.ticker);
  const expiration = robinhoodExpirationDate(input.expiration);
  const strike = Number(input.strike);
  const side = robinhoodOptionSide(input.side);
  if (!symbol || !expiration || !(strike > 0)) return null;

  const key = instrumentCacheKey(symbol, expiration, side, strike);
  const cached = instrumentIdCache.get(key);
  if (cached && Date.now() - cached.at < INSTRUMENT_CACHE_MS) return cached.id;

  try {
    const result = await callAgenticTool("get_option_instruments", {
      chain_symbol: symbol,
      expiration_dates: expiration,
      type: side,
      strike_price: strike.toFixed(4),
      state: "active",
    });
    const parsed = parseInstrumentRecord(result);
    if (!parsed) return null;
    instrumentIdCache.set(key, { id: parsed.id, chainId: parsed.chainId, at: Date.now() });
    return parsed.id;
  } catch {
    return null;
  }
}

function cachedInstrument(input: {
  ticker: string;
  expiration?: string | null;
  side?: string | null;
  strike?: number | null;
}): { id: string; chainId: string | null } | null {
  const symbol = robinhoodSymbol(input.ticker);
  const expiration = robinhoodExpirationDate(input.expiration);
  const strike = Number(input.strike);
  const side = robinhoodOptionSide(input.side);
  if (!symbol || !expiration || !(strike > 0)) return null;
  const cached = instrumentIdCache.get(instrumentCacheKey(symbol, expiration, side, strike));
  if (!cached || Date.now() - cached.at >= INSTRUMENT_CACHE_MS) return null;
  return cached;
}

function robinhoodApplink(route: string, params: Record<string, string | number | undefined> = {}): string {
  const url = new URL(`${ROBINHOOD_APPLINK_HOST}/u/${route}`);
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function cachedChainId(ticker: string): string | null {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  const cached = chainIdCache.get(symbol);
  if (!cached || Date.now() - cached.at >= INSTRUMENT_CACHE_MS) return null;
  return cached.id;
}

function parseChainId(result: unknown): string | null {
  const payload = unwrapToolPayload(result);
  const record =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.results)
      ? record.results
      : Array.isArray(record?.chains)
        ? record.chains
        : [];
  for (const item of rows) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const id = robinhoodOptionId((item as { id?: unknown }).id as string);
    if (id) return id;
  }
  return record ? robinhoodOptionId(record.id as string) : null;
}

async function resolveRobinhoodChainId(ticker: string): Promise<string | null> {
  const symbol = robinhoodSymbol(ticker);
  if (!symbol) return null;
  const cached = cachedChainId(symbol);
  if (cached) return cached;
  try {
    const result = await callAgenticTool("get_option_chains", { underlying_symbol: symbol });
    const id = parseChainId(result);
    if (!id) return null;
    chainIdCache.set(symbol, { id, at: Date.now() });
    return id;
  } catch {
    return null;
  }
}

export function isRobinhoodMobileHost(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const body = document.body;
    if (body?.classList.contains("sfe-mobile-ios") || body?.classList.contains("sfe-mobile-host")) {
      return true;
    }
    const path = window.location.pathname;
    return path === "/app" || path.startsWith("/app/");
  } catch {
    return false;
  }
}

function intentOptionSide(intent: Exclude<RobinhoodOpenIntent, { kind: "stock" }>): RobinhoodOptionSide {
  if (intent.kind === "sell-call") return "call";
  if (intent.kind === "sell-put") return "put";
  return robinhoodOptionSide(intent.side);
}

/**
 * Mobile href that opens the Robinhood app. `/u/stock` is not a real app
 * route (empty spinner). Stocks use robinhood.com/stocks/* (AASA).
 *
 * With an option instrument id we use `/u/option_strategy`, which opens that
 * contract's own page with Sell/Buy buttons. Its `strategy_code` is built the
 * way the app builds it (OptionStrategyCodesKt.toStrategyCode): the
 * instrument id, then `_`, then `L` for long or `S` for short, then the leg
 * ratio. A one-leg short is `<instrument-id>_S1`. Unlike `option_chain` this
 * ignores whatever ticket Robinhood had open last, so it is the only route
 * that reliably lands on the contract we asked for.
 *
 * Without an instrument id we fall back to `/u/option_chain`, which takes
 * only `chain_id`, `source` and `strike_price`. It has no side param and
 * restores the app's last put/call and buy/sell choice, so the side it opens
 * is whatever the user last traded.
 *
 * Routes that do NOT work, all verified on an iPhone:
 *   - `/u/option_position_open` and `/u/option_position_close` exist in the
 *     Android build but land on the app home here.
 *   - `/u/options_strategy_builder` is only the intro splash.
 *   - `source` on `option_chain` has no effect on the side.
 */
export function robinhoodAppHrefForIntent(intent: RobinhoodOpenIntent): string | null {
  const symbol = robinhoodSymbol(intent.ticker);
  if (!symbol) return null;

  if (intent.kind === "stock") {
    return robinhoodAppStockUrl(symbol);
  }

  const optionId = robinhoodOptionId(intent.optionId) ?? cachedInstrument(intent)?.id ?? null;
  if (optionId) {
    return robinhoodApplink("option_strategy", {
      strategy_code: `${optionId}_S1`,
      is_leg: "true",
    });
  }

  if (intent.kind === "btc") {
    return robinhoodAppStockUrl(symbol);
  }

  const knownChainId = robinhoodOptionId(intent.chainId);
  if (knownChainId) {
    const symbolKey = robinhoodSymbol(intent.ticker);
    if (symbolKey && !cachedChainId(symbolKey)) {
      chainIdCache.set(symbolKey, { id: knownChainId, at: Date.now() });
    }
  }

  const chainId =
    knownChainId ?? cachedInstrument(intent)?.chainId ?? cachedChainId(symbol);
  if (chainId) {
    const strike = Number(intent.strike);
    return robinhoodApplink("option_chain", {
      chain_id: chainId,
      strike_price: strike > 0 ? strike : undefined,
    });
  }

  return null;
}

/**
 * Immediate href: exact contract when we already have an instrument id,
 * otherwise the sell-put / sell-call builder (or stock page).
 */
export function robinhoodHrefForIntent(intent: RobinhoodOpenIntent): string | null {
  if (intent.kind === "stock") return robinhoodStockUrl(intent.ticker);

  const byId = robinhoodOptionInstrumentUrl(intent.optionId);
  if (byId) return byId;

  return robinhoodSellStrategyUrl(intent.ticker, intentOptionSide(intent));
}

export async function resolveRobinhoodHref(intent: RobinhoodOpenIntent): Promise<string | null> {
  if (intent.kind !== "stock" && !robinhoodOptionId(intent.optionId)) {
    await resolveRobinhoodOptionInstrumentId(intent);
  }
  if (
    isRobinhoodMobileHost() &&
    intent.kind !== "stock" &&
    !cachedInstrument(intent)?.chainId &&
    !robinhoodOptionId(intent.chainId) &&
    !cachedChainId(intent.ticker)
  ) {
    await resolveRobinhoodChainId(intent.ticker);
  }
  if (isRobinhoodMobileHost()) {
    return robinhoodAppHrefForIntent(intent);
  }
  const immediate = robinhoodHrefForIntent(intent);
  if (intent.kind === "stock") return immediate;
  if (robinhoodOptionInstrumentUrl(intent.optionId)) return immediate;
  return robinhoodOptionInstrumentUrl(cachedInstrument(intent)?.id) ?? immediate;
}

export function prefetchRobinhoodIntent(
  intent: RobinhoodOpenIntent | null | undefined,
  onResolved?: () => void,
): void {
  if (!intent || intent.kind === "stock") return;
  void (async () => {
    if (!robinhoodOptionId(intent.optionId)) {
      await resolveRobinhoodOptionInstrumentId(intent);
    }
    if (!cachedInstrument(intent)?.chainId && !cachedChainId(intent.ticker)) {
      await resolveRobinhoodChainId(intent.ticker);
    }
    onResolved?.();
  })();
}

export function robinhoodIntentFromRec(
  rec: {
    underlying_ticker?: string;
    side?: string;
    strike?: number;
    expiration_date?: string;
    option_id?: string;
    chain_id?: string;
  } | null | undefined,
  tableKind?: "puts" | "calls",
): RobinhoodOpenIntent | null {
  const ticker = rec?.underlying_ticker?.trim() ?? "";
  if (!ticker) return null;
  const side =
    tableKind === "calls"
      ? "call"
      : tableKind === "puts"
        ? "put"
        : robinhoodOptionSide(rec?.side);
  return {
    kind: side === "call" ? "sell-call" : "sell-put",
    ticker,
    side,
    strike: rec?.strike,
    expiration: rec?.expiration_date,
    optionId: rec?.option_id,
    chainId: rec?.chain_id,
  };
}

export function robinhoodIntentFromPosition(
  position: {
    ticker?: string;
    side?: string;
    strike?: number;
    exp_date?: string;
    option_id?: string;
    chain_id?: string;
  } | null | undefined,
): RobinhoodOpenIntent | null {
  const ticker = position?.ticker?.trim() ?? "";
  if (!ticker) return null;
  return {
    kind: "btc",
    ticker,
    side: position?.side,
    strike: position?.strike,
    expiration: position?.exp_date,
    optionId: position?.option_id,
    chainId: position?.chain_id,
  };
}

const RH_WARM_HOSTS = [
  "https://robinhood.com",
  "https://cdn.robinhood.com",
  "https://images.robinhood.com",
] as const;

/** Start TLS/DNS to Robinhood before the click so the first document is not a cold handshake. */
export function warmRobinhoodNetwork(): void {
  if (typeof document === "undefined") return;
  for (const href of RH_WARM_HOSTS) {
    if (document.head.querySelector(`link[data-sfe-rh-warm="${href}"]`)) continue;
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = href;
    preconnect.setAttribute("data-sfe-rh-warm", href);
    document.head.appendChild(preconnect);
    const dns = document.createElement("link");
    dns.rel = "dns-prefetch";
    dns.href = href;
    document.head.appendChild(dns);
  }
}

/** Best-effort document prefetch when the menu href is known. RH HTML is no-cache, so this only helps the race. */
export function prefetchRobinhoodPage(href: string): void {
  warmRobinhoodNetwork();
  const dest = String(href ?? "").trim();
  if (!dest || !isRobinhoodHref(dest) || typeof document === "undefined") return;
  let link = document.head.querySelector<HTMLLinkElement>("link[data-sfe-rh-doc-prefetch]");
  if (!link) {
    link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "document";
    link.setAttribute("data-sfe-rh-doc-prefetch", "1");
    document.head.appendChild(link);
  }
  if (link.href !== dest) link.href = dest;
}

function isRobinhoodHref(href: string): boolean {
  try {
    const dest = new URL(href);
    if (dest.protocol === "robinhood:") return true;
    if (dest.protocol !== "https:") return false;
    return (
      dest.hostname === "robinhood.com" ||
      dest.hostname === "www.robinhood.com" ||
      dest.hostname === "applink.robinhood.com"
    );
  } catch {
    return false;
  }
}

function openViaAnchor(href: string): boolean {
  // Same-tab navigation lets iOS/Android Universal Links hand off to the app.
  // window.open / target=_blank / a synthetic <a>.click() usually stay in Safari.
  try {
    window.location.assign(href);
    return true;
  } catch {
    return false;
  }
}

function rememberRobinhoodWindow(win: Window | null): Window | null {
  if (!win || win.closed) return lastRobinhoodWindow;
  lastRobinhoodWindow = win;
  return win;
}

function focusRobinhoodWindow(win: Window): void {
  try {
    win.focus();
  } catch {
    /* cross-origin panes can hide focus */
  }
}

function tryNavigateExisting(win: Window, href: string): boolean {
  if (win.closed) return false;
  focusRobinhoodWindow(win);
  try {
    win.location.assign(href);
    rememberRobinhoodWindow(win);
    return true;
  } catch {
    /* Cross-origin assign() throws. Robinhood also clears window.name, so a
       later named window.open() would spawn a second tab. */
  }
  try {
    const navigated = win.open(href, "_self");
    if (navigated) {
      rememberRobinhoodWindow(win);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function openNamedRobinhoodTab(href: string): Window | null {
  const win =
    loadRobinhoodOpenMode() === "popup"
      ? window.open(href, ROBINHOOD_WINDOW_NAME, ROBINHOOD_POPUP_FEATURES)
      : window.open(href, ROBINHOOD_WINDOW_NAME);
  return rememberRobinhoodWindow(win);
}

/**
 * Reuse one named Robinhood popup for every Open in Robinhood click.
 * A page cannot scan other tabs, so an RH tab the user opened themselves
 * cannot be claimed.
 */
export function openRobinhoodNearbyTab(url: string): boolean {
  const href = String(url ?? "").trim();
  if (!href || !isRobinhoodHref(href)) return false;

  if (isRobinhoodMobileHost()) {
    return openViaAnchor(href);
  }

  const desktop = getDesktopBridge();
  if (desktop?.openExternal) {
    void desktop.openExternal(href);
    return true;
  }

  warmRobinhoodNetwork();

  // If this tab was previously the named Robinhood pane, drop the name so
  // window.open(href, ROBINHOOD_WINDOW_NAME) cannot navigate StockForge itself.
  if (window.name === ROBINHOOD_WINDOW_NAME) {
    window.name = "";
  }

  if (lastRobinhoodWindow && !lastRobinhoodWindow.closed) {
    if (tryNavigateExisting(lastRobinhoodWindow, href)) return true;
  }

  // Named open navigates the same pane when window.name is still sfe-robinhood
  // (Chrome split view included). Robinhood may clear the name later; then this
  // would create a second tab, so we fall back to focusing the handle we have.
  const win = openNamedRobinhoodTab(href);
  if (win) {
    focusRobinhoodWindow(win);
    return true;
  }
  if (lastRobinhoodWindow && !lastRobinhoodWindow.closed) {
    focusRobinhoodWindow(lastRobinhoodWindow);
    return true;
  }
  return false;
}

/** Left-click without modifiers: reuse the existing RH pane in this gesture. */
export function consumeRobinhoodMenuClick(event: MouseEvent, href: string): boolean {
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const dest = String(href ?? "").trim();
  if (!dest || dest === "#" || dest.endsWith("#")) return false;
  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === "function") {
    event.stopImmediatePropagation();
  }
  return openRobinhoodNearbyTab(dest);
}

export function openRobinhoodOptionsChain(ticker: string): boolean {
  const url = robinhoodOptionsChainUrl(ticker);
  if (!url) return false;
  return openRobinhoodNearbyTab(url);
}

export function openRobinhoodStock(ticker: string): boolean {
  const url = robinhoodStockUrl(ticker);
  if (!url) return false;
  return openRobinhoodNearbyTab(url);
}

export async function openRobinhoodIntent(intent: RobinhoodOpenIntent): Promise<boolean> {
  if (isRobinhoodMobileHost()) {
    const ready = robinhoodAppHrefForIntent(intent);
    if (ready) return openRobinhoodNearbyTab(ready);
  }
  const url = await resolveRobinhoodHref(intent);
  if (!url) return false;
  return openRobinhoodNearbyTab(url);
}
